/**
 * Which platforms an application runs on.
 *
 * ── Why the vocabulary lives HERE and not in the auth service ────────
 *
 * Every other list this portal renders from a fixed set — platform roles, the
 * group-assignable subset — is fetched from the server, because the server
 * VALIDATES those and a client-side copy is a copy that stops agreeing the day
 * a name changes. This file exists to say why `platform` is the exception
 * rather than an oversight.
 *
 * Auth stores `platform` as `String @default("")` and never reads it. Nothing
 * on the server branches on it, no endpoint validates it, and the schema
 * comment on the neighbouring `status` column states the rule they share:
 * "free text rather than an enum so adding a lifecycle stage is not a
 * migration". A vocabulary the server does not hold cannot drift from the
 * server, so the portal owns it — and adding a sixth platform is one line here
 * rather than a migration and a deploy.
 *
 * ── It went from a picker to a free-text box, and that was a loss ────
 *
 * Jon, 2026-08-28: it used to be selectable. Free text on a field with five
 * real answers produces `Web`, `web`, `Browser` and `iOS · Android` in the same
 * column, which is unsortable, unfilterable and reads as three different facts.
 * Nothing was gained by the box: nobody has typed a platform this list does not
 * cover.
 *
 * ── An application is SEVERAL platforms, so this is a set ────────────
 *
 * Keyodo is a mobile app with a web front end. The free-text hint literally
 * suggested `"iOS · Android"`, so a single-choice dropdown would be a narrowing
 * rather than a restoration — it would force somebody to pick the one that
 * matters most and lose the rest.
 *
 * ── Stored in the one column it always had ───────────────────────────
 *
 * Comma-separated, canonical, lower case. NOT a schema change: the column is a
 * string, nothing parses it but this file, and a `String[]` migration would buy
 * a shape the server has no use for.
 *
 * The comma is worth one caution, because this repo has a scar from the last
 * one. org-admin#28 was roles joined with a comma and sent to an endpoint that
 * validated the joined string whole. The difference is that auth PARSES roles
 * and does not parse this — `platform` crosses the wire as an opaque string and
 * is written to a column verbatim. If auth ever starts reading it, this becomes
 * the server's vocabulary and this file becomes a copy.
 */

export type DeviceKind = 'phone' | 'tablet' | 'computer';
export type Platform = {
  value: string;
  label: string;
  /** Where it sits in the editor's grid. Web is its own row. */
  device: DeviceKind | 'web';
  system: 'android' | 'ios' | 'windows' | 'macos' | null;
};

/**
 * Phone and tablet are separate platforms (Jon, 2026-10-23): an app can be for
 * Android phones and not Android tablets, and a tester's devices are matched
 * the same way. Labelled "Phone: Android" so the kind of device reads first.
 *
 * In the order the editor's grid reads, row by row: Web, then phones, tablets,
 * computers.
 */
export const PLATFORMS: readonly Platform[] = [
  { value: 'web', label: 'Web', device: 'web', system: null },
  { value: 'android-phone', label: 'Phone: Android', device: 'phone', system: 'android' },
  { value: 'iphone', label: 'Phone: iOS', device: 'phone', system: 'ios' },
  { value: 'android-tablet', label: 'Tablet: Android', device: 'tablet', system: 'android' },
  { value: 'ipad', label: 'Tablet: iPadOS', device: 'tablet', system: 'ios' },
  { value: 'windows', label: 'Computer: Windows', device: 'computer', system: 'windows' },
  { value: 'macos', label: 'Computer: macOS', device: 'computer', system: 'macos' },
];

/**
 * Before phones and tablets were split, `android` and `ios` meant both. Read
 * from the column, each becomes its phone and tablet platforms, so an app
 * nobody has edited keeps every device it had, and its next save writes the
 * new values. auth reads the old values the same way.
 */
const LEGACY: ReadonlyMap<string, readonly string[]> = new Map([
  ['android', ['android-phone', 'android-tablet']],
  ['ios', ['iphone', 'ipad']],
]);

/** In a tester rule, where the old values can still be stored, they mean "either". */
export const LEGACY_LABEL: Record<string, string> = {
  android: 'Android: phone or tablet',
  ios: 'iOS: iPhone or iPad',
};
export function legacyExpansion(value: string): readonly string[] | undefined {
  return LEGACY.get(value);
}

const CANONICAL = new Map(PLATFORMS.map((p) => [p.value, p.value]));

/**
 * Spellings a person plausibly typed into the free-text box, mapped to the
 * value the picker uses.
 *
 * Deliberately short. This is not a general synonym engine — it exists so that
 * the handful of values already in the database resolve to a ticked box instead
 * of an "unknown" chip, and so that the first save after this ships is a
 * tidy-up rather than a loss. Anything not here survives verbatim; see
 * `parsePlatforms`.
 */
const ALIASES = new Map<string, string>([
  ['browser', 'web'],
  ['www', 'web'],
  ['mac', 'macos'],
  ['osx', 'macos'],
  ['os x', 'macos'],
  ['mac os', 'macos'],
  ['win', 'windows'],
]);

/**
 * What is stored, as a set of values.
 *
 * ── Every separator anybody actually used ────────────────────────────
 *
 * The old hint suggested `"iOS · Android"`, so the middot is not hypothetical —
 * it is the shape the portal itself told people to type. Slash, plus and
 * semicolon go in with it because they cost nothing and each one otherwise
 * produces a single unreadable token that a reader would have to retype.
 *
 * ── AN UNKNOWN TOKEN IS KEPT, NOT DROPPED ────────────────────────────
 *
 * The same rule `GrantCheckboxes` follows: a value that is in the database must
 * appear on the screen. Filtering to what this file recognises would make an
 * unrecognised platform vanish from the form while remaining in the column, and
 * the next save would then silently delete it — a write nobody asked for,
 * caused by a display decision.
 *
 * Exported and pure because that rule is the whole behaviour and breaking it is
 * invisible: the form would look right, and the loss would only show up in the
 * database.
 */
export function parsePlatforms(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return [
    ...new Set(
      raw
        .split(/[,·;/+]/)
        .map((t) => t.trim())
        .filter(Boolean)
        .flatMap((t) => {
          const lower = t.toLowerCase();
          return LEGACY.get(lower) ?? [CANONICAL.get(lower) ?? ALIASES.get(lower) ?? t];
        }),
    ),
  ];
}

/**
 * What to write back.
 *
 * Ordered by the vocabulary rather than by the order boxes were ticked, so two
 * applications on the same platforms store the same string and the column can
 * be compared and grouped. Unknown values keep their own order and go last —
 * they are not part of the vocabulary, so there is nowhere in it to sort them.
 *
 * An empty set is the empty string, which is the column's default and what
 * "not stated" has always looked like here.
 */
export function formatPlatforms(values: string[]): string {
  const order = PLATFORMS.map((p) => p.value);
  const known = order.filter((v) => values.includes(v));
  const unknown = values.filter((v) => !order.includes(v));
  return [...known, ...unknown].join(',');
}

/** The display name, or the value itself when it is not one of ours — the same
 *  fallback `roleLabel` makes, and for the same reason: showing the raw value
 *  beats inventing a label or rendering a blank. */
export function platformLabel(value: string): string {
  return PLATFORMS.find((p) => p.value === value)?.label ?? LEGACY_LABEL[value] ?? value;
}

/** Is this a value the picker knows? Used to mark the leftovers, so somebody
 *  editing an application can see which of its platforms came from the old text
 *  box and decide what it should have been. */
export function isKnownPlatform(value: string): boolean {
  return CANONICAL.has(value);
}
